import React, { useContext, useEffect, useState, useRef, useCallback } from 'react'
import { Container, ContainerOutlet, ContentWrapper } from './style'
import UserContext from '../../contexts/UserContext'
import jwtDecode, { JwtPayload } from 'jwt-decode'
import ModalContext from '../../contexts/ModalContext'
import BaseModal from '../../components/Modal'
import FeedbackComponent from '../../components/FeedbackComponent'
import { FeedbackContext } from '../../contexts/FeedbackContext'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'
import HeaderComponent from '../../components/Header'
import ChatSidebar from '../../components/ChatSidebar'
import { NotificationProvider } from '../../contexts/NotificationContext'
import TableFiltersContext from '../../contexts/TableFiltersContext'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { TableExportProvider } from '../../components/StandardPage/TableExportContext'

interface DecodedUser extends JwtPayload {
  email: string
  name: string
}

export interface TypeMainOutlet {
  search: string
  setSearch: React.Dispatch<React.SetStateAction<string>>
}

const getSearchKey = (pathname: string): string => {
  const cleaned = pathname
    .replace(/\/(create|export)$/, '')
    .replace(/\/(edit|start|view)\/\d+$/, '')
  return `search_${cleaned}`
}

const ClientPanel = () => {
  const { user } = useContext(UserContext)
  const { modalActive } = useContext(ModalContext)
  const { feedback } = useContext(FeedbackContext)
  const { setGlobalFilter } = useContext(TableFiltersContext)
  const { getPreference, setPreference, isLoading: isPrefsLoading } = useUserPreferences()
  const [search, setSearch] = useState<string>('')

  useEffect(() => {
    setGlobalFilter(search)
  }, [search, setGlobalFilter])
  const [isValidating, setIsValidating] = useState(true)
  const [isChatOpen, setIsChatOpen] = useState(false)
  const location = useLocation()
  const navigationType = useNavigationType()

  const lastPathname = useRef(location.pathname)
  const debounceRef = useRef<NodeJS.Timeout | null>(null)
  const initialLoadDone = useRef<Record<string, boolean>>({})

  const persistSearch = useCallback((pathname: string, value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setPreference(getSearchKey(pathname), value)
    }, 600)
  }, [setPreference])

  useEffect(() => {
    if (isPrefsLoading) return
    const key = getSearchKey(location.pathname)
    if (initialLoadDone.current[key]) return
    initialLoadDone.current[key] = true
    const saved = getPreference<string | undefined>(key)
    if (saved !== undefined && saved !== null) {
      setSearch(saved)
    }
  }, [isPrefsLoading, location.pathname, getPreference])

  useEffect(() => {
    const prevPath = lastPathname.current
    lastPathname.current = location.pathname

    if (location.pathname.endsWith('/export')) return
    if (prevPath === location.pathname) return

    if ((location.state as any)?.returnSearch !== undefined) {
      setSearch((location.state as any).returnSearch)
      return
    }

    const key = getSearchKey(location.pathname)
    const saved = getPreference<string | undefined>(key)
    setSearch(saved || '')
    initialLoadDone.current[key] = true
  }, [location.pathname])

  useEffect(() => {
    if (isPrefsLoading) return
    if (!initialLoadDone.current[getSearchKey(location.pathname)]) return
    persistSearch(location.pathname, search)
  }, [search])

  useEffect(() => {
    console.log('🔑 [CLIENT-PANEL] Montando componente, iniciando validação:', {
      hasUser: !!user,
      userId: user?.id || user?.sub,
      userEmail: user?.email,
      userApproved: user?.approved,
      userTenantId: user?.tenantId
    })
    
    const timer = setTimeout(() => {
      console.log('🔑 [CLIENT-PANEL] Timer de validação expirado, setando isValidating = false')
      setIsValidating(false)
    }, 200)
    
    return () => clearTimeout(timer)
  }, [])

  console.log('🔑 [CLIENT-PANEL] Render - Estado atual:', {
    isValidating,
    hasUser: !!user,
    userId: user?.id || user?.sub,
    userApproved: user?.approved,
    timestamp: new Date().toISOString()
  })

  if (isValidating) {
    console.log('🔑 [CLIENT-PANEL] Ainda validando, retornando null')
    return null
  }

  if (!user || (!user.id && !user.sub)) {
    console.log('🔑 [CLIENT-PANEL] Usuário não encontrado, retornando null')
    return null
  }

  if (user.approved && user.approved !== 'approved') {
    if (user.approved === 'pending') {
      console.log('⚠️ [CLIENT-PANEL] Usuário pendente, redirecionando para /accessvalidation')
      window.location.href = '/accessvalidation'
      return null
    } else if (user.approved === 'denied') {
      console.log('⚠️ [CLIENT-PANEL] Usuário negado, removendo token e redirecionando para /')
      localStorage.removeItem('token')
      window.location.href = '/'
      return null
    }
  }
  
  console.log('✅ [CLIENT-PANEL] Usuário aprovado, renderizando painel')

  console.log('✅ [CLIENT-PANEL] Renderizando painel com contexto:', { 
    contextSearch: search,
    outletActive: true 
  });
  
  return (
    <NotificationProvider>
      <TableExportProvider>
        <Container>
          <HeaderComponent
            search={search}
            setSearch={setSearch}
            isChatOpen={isChatOpen}
            onToggleChat={() => setIsChatOpen((v) => !v)}
          />
          {modalActive && <BaseModal />}
          {feedback.isOpen && <FeedbackComponent />}
          <ContentWrapper>
            <ContainerOutlet>
              <Outlet context={{ search, setSearch }} />
            </ContainerOutlet>
          </ContentWrapper>
          <ChatSidebar
            isOpen={isChatOpen}
            onClose={() => setIsChatOpen(false)}
          />
        </Container>
      </TableExportProvider>
    </NotificationProvider>
  )
}

export default ClientPanel
